import React, { useCallback, useContext, useEffect, useState } from 'react';
import styles from './PlaceOrder.module.css';
import { useDispatch, useSelector } from 'react-redux';
import "react-datepicker/dist/react-datepicker.css";

import { InfoTooltip, StopSummaryTooltip } from './common';
import { OrderContext } from './PlaceOrder';
import { buildDropOffSummary } from './stopSummary';
import { createOrder, deleteOrder, updateOrder } from './orderAPI';
import { handleGetOrdersByOrderGroup, handleOnSortOrder, ordersState, setDisableCreateOrder, setShowSavedDropOffAddrModal } from './placeOrderSlice';
import { toLocation, useAddressAutocomplete } from './addressAutocomplete';

import closeIcon from "../../images/place_order/close-icon.png";
import savedAddressIcon from "../../images/place_order/saved-address-icon.png";

export function Order(props) {
  const dispatch = useDispatch();
  const orderContext = useContext(OrderContext);
  const orderListState = useSelector(ordersState);
  const { currIdx, addStop, editOrder: editOrderProps, nextOrdinal, showThread, handleOrders, handleAddressChange, closeDropOffModal, showDropOffModal, handleError } = props;

  const [dropOffForm, setDropOffForm] = useState({
    ordinal: "",
    recipientName: orderContext.recipientName ? orderContext.recipientName : "",
    recipientPhone: orderContext.recipientPhone ? orderContext.recipientPhone : "",
    deliverDateTime: orderContext.deliverDateTime ? orderContext.deliverDateTime : "",
    dropOffLocation: orderContext.dropOffLocation,
    isCodEnabled: orderContext.isCodEnabled ? orderContext.isCodEnabled : false,
    codAmount: orderContext.codAmount ? orderContext.codAmount : 0,
    doorToDoor: orderContext.doorToDoor ? orderContext.doorToDoor : "",
  });
  const [disableDeleteOrder, setDisableDeleteOrder] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [sequenceValue, setSequenceValue] = useState(currIdx + 1);

  useEffect(() => {
    setSequenceValue(currIdx + 1);
  }, [currIdx]);

  const commitSequence = () => {
    const totalStops = orderListState.orderGroup.orders.length;
    const newPosition = parseInt(sequenceValue, 10);

    if (isNaN(newPosition) || newPosition < 1 || newPosition > totalStops) {
      setSequenceValue(currIdx + 1);
      return;
    }

    if (newPosition - 1 !== currIdx) {
      dispatch(handleOnSortOrder(currIdx, newPosition - 1));
    }
  }

  useEffect(() => {
    setDropOffForm({
      ...dropOffForm,
      recipientName: orderContext.recipientName,
      recipientPhone: orderContext.recipientPhone,
      deliverDateTime: orderContext.deliverDateTime,
      dropOffLocation: orderContext.dropOffLocation,
      isCodEnabled: orderContext.isCodEnabled,
      codAmount: orderContext.codAmount,
      doorToDoor: orderContext.doorToDoor,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderContext]);

  const validateFields = useCallback(() => {
    let error = false;

    if (dropOffForm.dropOffLocation.country === null || dropOffForm.dropOffLocation.country === "") {
      error = true;
      handleError(true, "Please re-select the drop-off address from drop-down list.");
      return error;
    }

    for (var key in dropOffForm) {
      if (key !== "ordinal" && key !== "hadDelivered" && key !== "codAmount" && key !== "assigned" && key !== "receiptNumber" && key !== "deliveryRange" && key !== "doorToDoor" && key !== "noteToDriver" && key !== "doorToDoorFee") {
        if (dropOffForm[key] === null || dropOffForm[key] === "") {
          error = true;
          handleError(true, "Please complete all the fields.");
          return error;
        }
      }
    }
    return error;
  }, [dropOffForm, handleError]);

  const editOrder = useCallback(async (location) => {
    const error = validateFields();
    if (!error) {
      try {
        if (orderContext.id !== "") {
          // Update Order
          let orderPayload = {
            ...orderContext,
            deliverDateTime: new Date(orderContext.deliverDateTime).toISOString(),
            dropOffLocation: location
          }
          await updateOrder(orderListState.orderGroup.id, orderPayload, orderContext.id);
          dispatch(handleGetOrdersByOrderGroup(orderListState.orderGroup.id));
          handleError(false, "");
        }
      } catch (error) {
        handleError(true, error.message);
      }
    }
  }, [dispatch, handleError, orderContext, orderListState, validateFields]);

  useAddressAutocomplete(`dropOffAutocomplete-${currIdx}`, (place) => {
    try {
      const location = toLocation(place);

      setDropOffForm(prevDropOffForm => ({
        ...prevDropOffForm,
        dropOffLocation: {
          ...prevDropOffForm.dropOffLocation,
          ...location
        }
      }));

      if (orderContext.id === undefined) {
        handleAddressChange(location);
        showDropOffModal(currIdx);
      } else {
        editOrder(location);
      }
    } catch (error) {
      handleError(true, "Please select a drop-off address from the drop-down list.");
    }
  });

  const submitOrder = useCallback(async () => {
    const error = validateFields();
    if (!error) {
      try {
        if (!orderListState.disableCreateOrder && orderListState.orderGroup.id !== null && addStop) {
          dispatch(setDisableCreateOrder(true));

          // Create Order
          let orderPayload = {
            ...dropOffForm,
            ordinal: nextOrdinal,
            deliverDateTime: new Date(dropOffForm.deliverDateTime).toISOString(),
            codAmount: dropOffForm.isCodEnabled ? dropOffForm.codAmount : ""
          }

          await createOrder(orderListState.orderGroup.id, orderPayload);
          dispatch(setDisableCreateOrder(false));
          dispatch(handleGetOrdersByOrderGroup(orderListState.orderGroup.id));

          handleOrders();
          handleError(false, "");
        }
      } catch (error) {
        handleError(true, error.message);
      }
    }
  }, [addStop, dispatch, dropOffForm, handleError, handleOrders, nextOrdinal, orderListState, validateFields]);

  useEffect(() => {
    if (
      !formSubmitted
      && orderListState.orderGroup.id !== null
      && dropOffForm.recipientName !== ""
      && dropOffForm.recipientPhone !== ""
      && dropOffForm.deliverDateTime !== ""
      && dropOffForm.dropOffLocation.coordinate.latitude !== ""
      && dropOffForm.dropOffLocation.coordinate.longitude !== ""
    ) {
      setFormSubmitted(true);
      submitOrder();
    }
  }, [formSubmitted, dropOffForm, orderListState, submitOrder]);


  const removeOrder = async () => {
    setDisableDeleteOrder(true);

    try {
      if (!disableDeleteOrder && orderContext.id && orderContext.id !== "") {
        // Delete order
        await deleteOrder(orderListState.orderGroup.id, orderContext.id);
        dispatch(handleGetOrdersByOrderGroup(orderListState.orderGroup.id));
        handleError(false, "");
      }
    } catch (error) {
      handleError(true, error.message);
    } finally {
      setDisableDeleteOrder(false);
    }
  }

  const currency = (orderContext.currency === "MYR" || !orderContext.currency) ? "RM" : orderContext.currency;
  const hasDetails = Boolean(dropOffForm.recipientName);
  const hasAddress = Boolean(dropOffForm.dropOffLocation.address);
  const summaryRows = buildDropOffSummary({ ...orderContext, ...dropOffForm }, currency);

  return (
    <div className={`${styles.poStop} ${editOrderProps ? styles.poStopActive : ""}`}>
      <div className={styles.poRail}>
        <div className={`${styles.poPin} ${styles.poPinDropOff}`}>{currIdx + 1}</div>
        {showThread && <div className={styles.poThread} />}
      </div>

      <div className={styles.poStopMain}>
        <input
          className={styles.poAddrInput}
          id={`dropOffAutocomplete-${currIdx}`}
          name="dropOffAddress"
          value={dropOffForm.dropOffLocation.address}
          onChange={(e) =>
            setDropOffForm({
              ...dropOffForm,
              dropOffLocation: {
                ...dropOffForm.dropOffLocation,
                address: e.target.value
              }
            })
          }
          placeholder="Drop-off location"
          autoComplete="off"
        />

        <div id={`editDetails-${currIdx}`} className={styles.poMeta} onClick={() => showDropOffModal(currIdx)}>
          {
            hasDetails
              ? <>
                <span id={`detailsString-${currIdx}`} className={styles.poMetaText}>
                  {dropOffForm.recipientName}&nbsp;&nbsp;·&nbsp;&nbsp;{dropOffForm.recipientPhone}
                  {dropOffForm.dropOffLocation.unitNumber ? `\u00a0\u00a0·\u00a0\u00a0${dropOffForm.dropOffLocation.unitNumber}` : ""}
                </span>
                {orderContext.doorToDoorFee > 0 && <span className={styles.poChip}>Door-to-door {currency} {(orderContext.doorToDoorFee / 100).toFixed(2)}</span>}
                {orderContext.codAmount > 0 && <span className={`${styles.poChip} ${styles.poChipCod}`}>COD {currency} {(orderContext.codAmount / 100).toFixed(2)}</span>}
                {orderContext.deliveryFee > 0 && <span className={styles.poChip}>{currency} {(orderContext.deliveryFee / 100).toFixed(2)}</span>}
              </>
              : hasAddress
                ? <span id={`enterDetails-${currIdx}`} className={styles.poMissing}>
                  <i className="fas fa-exclamation-circle" style={{ fontSize: 12 }} />
                  Add contact details
                </span>
                : null
          }
          <StopSummaryTooltip rows={summaryRows} />
        </div>
      </div>

      <div className={styles.poStopActs}>
        {
          !addStop &&
          <InfoTooltip title="Type a position to move this stop, or drag the handle." placement="top" arrow>
            <input
              id={`sequenceInput-${currIdx}`}
              className={styles.poSeqInput}
              type="number"
              min={1}
              max={orderListState.orderGroup.orders.length}
              value={sequenceValue}
              onChange={(e) => setSequenceValue(e.target.value)}
              onBlur={commitSequence}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
            />
          </InfoTooltip>
        }
        {
          addStop &&
          <InfoTooltip title="Saved drop-off addresses" placement="top" arrow>
            <button
              id={`addrBookIcon-${currIdx}`}
              className={styles.poIco}
              onClick={() => { closeDropOffModal(); dispatch(setShowSavedDropOffAddrModal(true)); }}
            >
              <img src={savedAddressIcon} alt="saved addresses" />
            </button>
          </InfoTooltip>
        }
        {
          !addStop &&
          <InfoTooltip title="Remove this stop" placement="top" arrow>
            <button
              id={`deleteIcon-${currIdx}`}
              className={styles.poIco}
              onClick={() => !disableDeleteOrder && removeOrder()}
            >
              <img src={closeIcon} alt="remove" />
            </button>
          </InfoTooltip>
        }
      </div>
    </div>
  )
}
